/**
 * Formatadores compartilhados — usados em toda a aplicação.
 */

import { ADICIONAIS_BOOLEAN, ADICIONAIS_QUANTIDADE, BASES } from '../data/adicionais'

// Flags antigas que agora viraram parte do campo `base` (radio).
// O resumo do móvel mostra "Base ..." UMA vez via BASES — pula os
// booleans equivalentes pra não duplicar.
const FLAGS_DE_BASE = new Set(['baseMadeira', 'baseMetalon', 'pePalito', 'rodinhas'])

const _moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

/** R$ 1.234,56 */
export const fmtMoeda = (v) => _moeda.format(v || 0)

/** Alias histórico — mesmo que fmtMoeda */
export const fmt             = fmtMoeda
export const formatCurrency  = fmtMoeda

/**
 * Telefone padrão brasileiro: (99) 99999-9999 (celular 11 dígitos) ou
 * (99) 9999-9999 (fixo 10 dígitos). Aceita qualquer entrada com ou sem
 * formatação, máscara ou +55. Retorna string vazia se não houver dígitos.
 *
 * Exemplos:
 *   '84992066156'      → '(84) 99206-6156'
 *   '(84) 99206 6156'  → '(84) 99206-6156'
 *   '+5583999921504'   → '(83) 99992-1504'
 *   '8334442222'       → '(83) 3444-2222'
 */
export function formatTelefone(input) {
  if (input == null || input === '') return ''
  let d = String(input).replace(/\D/g, '')
  // Remove DDI 55 se vier com prefixo
  if (d.length === 13 && d.startsWith('55')) d = d.slice(2)
  if (d.length === 12 && d.startsWith('55')) d = d.slice(2)
  if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`
  if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`
  // Parcialmente digitado — máscara progressiva
  if (d.length > 7) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7,11)}`
  if (d.length > 2) return `(${d.slice(0,2)}) ${d.slice(2)}`
  if (d.length > 0) return `(${d}`
  return ''
}

// Mapa opcional: reescreve como o modelo aparece no PDF/WhatsApp
// (nome comercial + sufixo descritivo). NÃO altera m.modelo interno
// — geometria, cálculo, kanban continuam usando o nome canônico.
// Só afeta a apresentação pra o cliente.
//
// Adicione aqui novas categorias conforme a vendedora refinar.
export const DESCRICAO_COMERCIAL = {
  'Mesa Home Office Sem Gavetas':    { nome: 'Mesa Home Office', sufixo: 'sem gavetas' },
  'Mesa Home Office 1 Gaveta':       { nome: 'Mesa Home Office', sufixo: 'com uma gaveta' },
  'Mesa Home Office 2 Gavetas':      { nome: 'Mesa Home Office', sufixo: 'com duas gavetas' },
  'Mesa Home Office Gabinete':       { nome: 'Mesa Home Office', sufixo: 'com uma gaveta e uma porta' },
  'Mesa Home Office Nicho Duplo':    { nome: 'Mesa Home Office', sufixo: 'com um nicho duplo lateral' },
  'Mesa Home Office Nicho e Gaveta': { nome: 'Mesa Home Office', sufixo: 'com uma gaveta e um nicho duplo lateral' },
  'Mesa Bela':                       { nome: 'Mesa Home Office', sufixo: 'com 3 gavetas laterais' },
  'Mesa Bela Completa':              { nome: 'Mesa Home Office', sufixo: 'com 3 gavetas laterais e uma gaveta superior' },

  // MESAS EM L
  'Mesa em L':                       { nome: 'Mesa em L', sufixo: 'em MDF 25mm de espessura' },
  'Mesa em L com Gaveteiro':         { nome: 'Mesa em L', sufixo: 'em MDF 25mm de espessura, com 3 gavetas laterais' },

  // MESA DUPLA
  'Mesa Dupla':                      { nome: 'Mesa dupla', sufixo: 'com nicho duplo central' },

  // MESAS DE REUNIÃO
  'Mesa de Reunião com Laterais':    { nome: 'Mesa de reunião', sufixo: 'em MDF 25mm de espessura, com pernas laterais e saia centralizada' },
  'Mesa de Reunião Pé Central':      { nome: 'Mesa de reunião', sufixo: 'em MDF 25mm de espessura, com pernas e saia centrais' },

  // MESAS DE JANTAR
  'Mesa de Jantar Base Cúbica':      { nome: 'Mesa de jantar quadrada', sufixo: 'com base cúbica e tampo em MDF 25mm de espessura' },
  'Mesa de Jantar Base Alongada':    { nome: 'Mesa de jantar retangular', sufixo: 'com base alongada e tampo em MDF 25mm de espessura' },

  // MESAS DE CABECEIRA
  'Cabeceira com Prateleira':               { nome: 'Mesa de cabeceira', sufixo: 'com uma prateleira' },
  'Cabeceira com Nicho e 1 Gaveta':         { nome: 'Mesa de cabeceira', sufixo: 'com um nicho e uma gaveta inferior' },
  'Cabeceira com 1 Gaveta e Nicho Inferior':{ nome: 'Mesa de cabeceira', sufixo: 'com uma gaveta e um nicho inferior' },
  'Cabeceira com Nicho e 2 Gavetas':        { nome: 'Mesa de cabeceira', sufixo: 'com um nicho superior e duas gavetas' },
  'Cabeceira com 2 Gavetas':                { nome: 'Mesa de cabeceira', sufixo: 'com 2 gavetas' },
  'Cabeceira com 3 Gavetas':                { nome: 'Mesa de cabeceira', sufixo: 'com 3 gavetas' },

  // APARADORES
  'Aparador Básico':                         { nome: 'Aparador básico', sufixo: 'em MDF 25mm de espessura' },
  'Aparador com Prateleira':                 { nome: 'Aparador',        sufixo: 'em MDF 25mm de espessura, com uma prateleira' },

  // ESTANTES ALTAS SIMPLES
  'Estante 4 Andares':                       { nome: 'Estante', sufixo: 'com 4 espaços' },
  'Estante 5 Andares':                       { nome: 'Estante', sufixo: 'com 5 espaços' },
  'Estante com 2 Portas Inferiores':         { nome: 'Estante', sufixo: 'com 3 espaços superiores e duas portas inferiores c/ prateleira interna' },

  // ESTANTES ALTAS MÉDIAS
  'Estante Assimétrica 5A 10 espaços':       { nome: 'Estante Assimétrica', sufixo: 'com 5 andares e 10 espaços desencontrados' },
  'Estante Assimétrica 4A 10 espaços':       { nome: 'Estante Assimétrica', sufixo: 'com 4 andares e 10 espaços desencontrados' },
  'Estante Assimétrica 5A 13 espaços':       { nome: 'Estante Assimétrica', sufixo: 'com 5 andares e 13 espaços desencontrados' },
  'Estante Dupla 4 Andares':                 { nome: 'Estante dupla',       sufixo: 'com 4 andares e 8 nichos' },
  'Estante Dupla 5 Andares':                 { nome: 'Estante dupla',       sufixo: 'com 5 andares e 10 nichos' },
  'Estante Tripla 5 Andares':                { nome: 'Estante tripla',      sufixo: 'com 5 andares e 15 nichos' },

  // ESTANTES BAIXAS
  'Estante Baixa 3 Andares':                 { nome: 'Estante baixa',            sufixo: 'com 3 espaços' },
  'Estante Baixa Dupla 3 Andares':           { nome: 'Estante baixa dupla',      sufixo: 'com 3 andares e 6 nichos' },
  'Estante Baixa Assimétrica':               { nome: 'Estante baixa assimétrica',sufixo: 'com 3 andares e 6 nichos desencontrados' },
  'Estante Baixa Quádrupla':                 { nome: 'Estante baixa horizontal', sufixo: 'com 8 nichos' },

  // NICHOS
  'Nicho Vertical com Prateleira':           { nome: 'Nicho duplo vertical',   sufixo: 'com 1 prateleira' },
  'Nicho Horizontal com Divisória':          { nome: 'Nicho duplo horizontal', sufixo: 'com 1 divisória vertical' },
  'Nicho com Porta Basculante':              { nome: 'Nicho horizontal',       sufixo: 'com uma porta basculante' },
  'Nicho Duplo com 2 Portas Basculantes':    { nome: 'Nicho duplo horizontal', sufixo: 'com 2 portas basculantes e 2 espaços' },

  // ESPECIAIS
  'Armário Estante 4 Andares':               { nome: 'Armário estante', sufixo: 'com uma porta e 8 nichos' },
  'Armário Estante 5 Andares':               { nome: 'Armário estante', sufixo: 'com uma porta e 10 nichos' },

  // ROUPEIROS 2 PORTAS
  'Roupeiro 2 Portas Cabideiro e Prateleiras': { nome: 'Roupeiro', sufixo: 'com duas portas, maleiro interno, cabideiro e 2 espaços inferiores' },
  'Roupeiro 2 Portas 5 Espaços':               { nome: 'Roupeiro', sufixo: 'com duas portas e 5 espaços internos' },
  'Roupeiro 2 Portas Cabideiro e 7 Nichos':    { nome: 'Roupeiro', sufixo: 'com duas portas, maleiro interno, cabideiro e 7 nichos' },
  'Roupeiro 2 Portas Cabideiro e 2 Gavetas':   { nome: 'Roupeiro', sufixo: 'com duas portas, maleiro interno, cabideiro e 2 gavetas inferiores' },
  'Roupeiro 2 Portas 4 Espaços e 2 Gavetas':   { nome: 'Roupeiro', sufixo: 'com duas portas, 4 espaços e 2 gavetas inferiores' },
  'Roupeiro 2 Portas Cabideiro e 3 Gavetas':   { nome: 'Roupeiro', sufixo: 'com duas portas, maleiro interno, cabideiro e 3 gavetas inferiores' },

  // CÔMODAS
  'Cômoda 4 Gavetas':                          { nome: 'Cômoda',       sufixo: 'com 4 gavetas' },
  'Cômoda 5 Gavetas':                          { nome: 'Cômoda',       sufixo: 'com 5 gavetas' },
  'Cômoda 5 Gavetas com Porta':                { nome: 'Cômoda',       sufixo: 'com 5 gavetas e uma porta com 2 prateleiras internas' },
  'Cômoda Dupla 8 Gavetas':                    { nome: 'Cômoda dupla', sufixo: 'com 8 gavetas' },

  // SAPATEIRAS
  'Sapateira Baixa 6 Nichos':                  { nome: 'Sapateira baixa',        sufixo: 'com 6 nichos' },
  'Sapateira Baixa 8 Nichos':                  { nome: 'Sapateira baixa',        sufixo: 'com 8 nichos' },
  'Sapateira Baixa 1 Porta':                   { nome: 'Sapateira baixa',        sufixo: 'com uma porta e 4 espaços internos' },
  'Sapateira Baixa 2 Portas':                  { nome: 'Sapateira baixa',        sufixo: 'com duas portas e 4 espaços internos' },
  'Sapateira Alta 1 Porta':                    { nome: 'Sapateira alta',         sufixo: 'com uma porta e 8 espaços internos' },
  'Sapateira Alta 2 Portas':                   { nome: 'Sapateira alta',         sufixo: 'com duas portas e 8 espaços internos' },
  'Sapateira Basculante 1 Porta':              { nome: 'Sapateira basculante',   sufixo: 'em MDF 25mm, com uma porta sistema de pistões' },
  'Sapateira Basculante 3 Portas':             { nome: 'Sapateira basculante',   sufixo: 'com 3 portas sistema de pistões' },
  'Sapateira Basculante Dupla 6 Portas':       { nome: 'Sapateira basculante dupla', sufixo: 'com 6 portas sistema de pistões' },

  // BAÚS
  'Baú Solteiro':                              { nome: 'Baú p/ cama solteiro', sufixo: 'com tampa' },
  'Baú Casal':                                 { nome: 'Baú p/ cama casal',    sufixo: 'com tampa' },

  // BALCÕES
  'Balcão Alto Básico':                        { nome: 'Balcão alto básico', sufixo: 'com dois níveis de apoio' },
  'Balcão Alto com Nicho e Gaveta':            { nome: 'Balcão alto',        sufixo: 'com dois níveis de apoio, com uma gaveta e dois nichos laterais' },
  'Balcão Médio com Gaveta e Nicho':           { nome: 'Balcão médio',       sufixo: 'com uma gaveta e dois espaços. Tampo com laterais passantes' },
  'Balcão Médio Misto':                        { nome: 'Balcão médio',       sufixo: 'com uma gaveta, dois nichos e uma porta com prateleira interna. Tampo com laterais passantes' },

  // BUFFETS 2 PORTAS
  'Buffet 2 Portas':                           { nome: 'Buffet', sufixo: 'com duas portas e uma prateleira interna' },
  'Buffet 1 Porta com Nicho':                  { nome: 'Buffet', sufixo: 'com dois nichos e uma porta com uma prateleira interna. Tampo laterais passantes' },
  'Buffet 2 Portas com Espaço Eletro':         { nome: 'Buffet', sufixo: 'com duas portas e uma prateleira interna. Com espaço lateral p/ eletro. Bancada e laterais em MDF 25mm de espessura' },

  // BUFFETS 3 PORTAS
  'Buffet 3 Portas':                           { nome: 'Buffet', sufixo: 'com 3 portas e uma prateleira interna' },
  'Buffet 3 Portas com Gavetas':               { nome: 'Buffet', sufixo: 'com 3 portas e uma prateleira interna. Com 3 gavetas laterais' },

  // BUFFETS 4 PORTAS
  'Buffet 4 Portas':                           { nome: 'Buffet', sufixo: 'com 4 portas e prateleira interna' },
  'Buffet 4 Portas Adega':                     { nome: 'Buffet', sufixo: 'com 4 portas e prateleira interna. Com 10 nichos p/ garrafas no meio. Laterais e puxador passante' },

  // RACKS
  'Rack 2 Basculantes e 2 Nichos':             { nome: 'Rack', sufixo: 'com dois nichos superiores e 2 portas basculantes. Sem prateleira interna' },
  'Rack Thalita':                              { nome: 'Rack', sufixo: 'com dois nichos e 4 portas, sem prateleiras interna. Estrutura em MDF 25mm de espessura' },
  'Rack 4 Portas':                             { nome: 'Rack', sufixo: 'com 4 portas e prateleira interna' },
  'Rack 2 Portas e Nicho Duplo Central':       { nome: 'Rack', sufixo: 'com 2 portas sem prateleira interna, e dois nichos centrais' },

  // ARMÁRIOS MULTIUSO — sufixo dinâmico: X = número total de prateleiras
  // preenchidas (soma prateleiraCor + prateleiraBranco dos adicionais).
  'Armário Multiuso 2 Portas': {
    nome:   'Armário multiuso',
    sufixo: m => {
      const x = Number(m?.adicionais?.prateleiraCor || 0) + Number(m?.adicionais?.prateleiraBranco || 0)
      return x > 0
        ? `com duas portas e ${x} prateleira${x === 1 ? '' : 's'} interna${x === 1 ? '' : 's'}`
        : 'com duas portas'
    },
  },
  'Armário Multiuso 1 Porta': {
    nome:   'Armário multiuso',
    sufixo: m => {
      const x = Number(m?.adicionais?.prateleiraCor || 0) + Number(m?.adicionais?.prateleiraBranco || 0)
      return x > 0
        ? `com uma porta e ${x} prateleira${x === 1 ? '' : 's'} interna${x === 1 ? '' : 's'}`
        : 'com uma porta'
    },
  },

  // ARMÁRIOS VERTICAIS
  'Armário Copa':                              { nome: 'Armário vertical', sufixo: 'com uma porta basculante superior, dois nichos centrais, duas portas inferiores c/ prateleira interna' },
  'Armário com Nicho Central':                 { nome: 'Armário vertical', sufixo: 'com duas portas superiores c/ prateleira interna, um nicho central, duas portas inferiores c/ prateleira interna' },
  'Armário 3 Andares e Portas Inferiores':     { nome: 'Armário vertical', sufixo: 'com 3 espaços superiores, duas portas inferiores c/ prateleira interna' },

  // ARMÁRIOS SUSPENSOS
  'Armário Suspenso 2 Basculantes e 2 Nichos': { nome: 'Armário superior suspenso', sufixo: 'com duas portas basculantes e dois nichos' },
  'Armário Suspenso 2 Portas e Basculante':    { nome: 'Armário superior suspenso', sufixo: 'com duas portas e prateleira interna, com uma porta basculante e um nicho' },
  'Armário Suspenso 3 Portas':                 { nome: 'Armário superior suspenso', sufixo: 'com 3 portas e prateleira interna' },
  'Armário Suspenso 2 Portas e Nicho':         { nome: 'Armário superior suspenso', sufixo: 'com duas portas e prateleira interna, com dois nichos laterais' },
  'Armário Suspenso 3 Portas e Nicho':         { nome: 'Armário superior suspenso', sufixo: 'com 3 portas e prateleira interna, com dois nichos laterais' },
  'Armário Suspenso 4 Portas':                 { nome: 'Armário superior suspenso', sufixo: 'com 4 portas e prateleira interna' },

  // GELÁGUA
  'Móvel Gelágua com Fruteira':                { nome: 'Móvel p/ gelágua', sufixo: 'com uma porta superior sem prateleira interna, com dois espaços inferiores com testeira' },
  'Móvel Gelágua Fechado':                     { nome: 'Móvel p/ gelágua', sufixo: 'com uma porta superior e inferior sem prateleira interna' },

  // ÁREA DE SERVIÇO
  'Armário Área de Serviço 2 Portas':          { nome: 'Armário p/ área de serviço', sufixo: 'com duas portas, 6 nichos e um espaço p/ vassouras' },
  'Armário Área de Serviço 1 Porta':           { nome: 'Armário p/ área de serviço', sufixo: 'com uma porta, 5 nichos e um espaço p/ vassouras' },
  'Armário para Máquina Top Load':             { nome: 'Armário p/ máquina top load',   sufixo: 'com uma porta superior basculante e uma prateleira' },
  'Armário para Máquina Front Load':           { nome: 'Armário p/ máquina front load', sufixo: 'com duas portas superiores e prateleira interna, e um nicho' },

  // ARMÁRIOS WC
  'Carrinho WC com Porta Basculante':          { nome: 'Carrinho WC', sufixo: 'com uma porta basculante' },
  'Carrinho WC com Porta e Nicho':             { nome: 'Carrinho WC', sufixo: 'com uma porta e um nicho' },
  'Carrinho WC Aberto':                        { nome: 'Carrinho WC', sufixo: 'sem porta' },

  // CARRINHO DE CAFÉ
  'Carrinho de Café':                          { nome: 'Carrinho de café/bar', sufixo: '' },
  'Carrinho de Café com Gaveta':               { nome: 'Carrinho de café/bar', sufixo: 'com uma gaveta' },
}

/**
 * Descrição padronizada de um móvel:
 * {Modelo} {Cor} ({C}x{P}x{A}) {sufixo comercial} {adicionais · } {observações}
 *
 * Exemplo: Armário de vassouras Branco (90x40x190) Rodinhas · Puxador perfil prata
 * Com mapa: Mesa Home Office Branco (120x60x75) sem gavetas
 */
export function descricaoMovel(m) {
  const partes  = []
  const comerc  = DESCRICAO_COMERCIAL[m.modelo]

  // Modelo (nome comercial se mapeado; caso contrário nome canônico) + cor
  if (m.modelo) partes.push(comerc?.nome || m.modelo)
  if (m.cor)    partes.push(m.cor)

  // Medidas em cm: C x P x A (comprimento × profundidade × altura)
  const c = m.comprimento2 && Number(m.comprimento2) > 0
    ? `${m.comprimento}+${m.comprimento2}`
    : m.comprimento
  const medidas = [c, m.profundidade, m.altura].filter(Boolean).join('x')
  if (medidas) partes.push(`(${medidas})`)

  // Sufixo descritivo do mapa comercial — pode ser string estática ou
  // função(m) → string, pra casos onde a descrição depende de campos
  // dinâmicos do móvel (ex: "X prateleiras" onde X vem de adicionais).
  const sufixoResolvido = typeof comerc?.sufixo === 'function'
    ? comerc.sufixo(m)
    : comerc?.sufixo
  if (sufixoResolvido) partes.push(sufixoResolvido)

  // Adicionais
  const ads = []
  // Puxador — "Sem puxador" também aparece como escolha explícita
  if (m.adicionais?.puxadorTipo) {
    if (m.adicionais.puxadorTipo === 'Sem puxador') {
      ads.push('Sem puxador')
    } else {
      const cor = m.adicionais.puxadorCor ? ` ${m.adicionais.puxadorCor}` : ''
      const qtd = m.adicionais.puxadorQtd > 0 ? ` (${m.adicionais.puxadorQtd}x)` : ''
      ads.push(`Puxador ${m.adicionais.puxadorTipo}${cor}${qtd}`)
    }
  }
  // Base — sempre aparece UMA linha consolidada (inclusive "Sem base ...")
  const baseId = m.adicionais?.base
  if (baseId) {
    const baseLabel = BASES.find(b => b.id === baseId)?.label
    if (baseLabel) ads.push(baseLabel)
  }
  // Booleans extras — pula os que viraram base. Pula moldura/25mm se semAdicionais.
  ADICIONAIS_BOOLEAN.forEach(({ key, label }) => {
    if (baseId && FLAGS_DE_BASE.has(key)) return
    if (m.adicionais?.semAdicionais && (key === 'moldura' || key === 'espessura25mm')) return
    if (m.adicionais?.[key]) ads.push(label)
  })
  ADICIONAIS_QUANTIDADE.forEach(({ key, label }) => {
    if (m.adicionais?.[key] > 0) ads.push(`${label}: ${m.adicionais[key]}`)
  })
  if (ads.length > 0) partes.push(ads.join(' · '))

  // Observações
  if (m.observacoes) partes.push(m.observacoes)

  return partes.join(' ')
}

// Expande siglas de cor (BRC → Branco) usadas na descrição do Bling.
// Pedidos vindos do Bling têm o resumo montado por nomeProdutoBling() em
// bling.js, que abrevia cor pra caber no limite de caracteres do Bling.
// Na Logística e no PDF do entregador o entregador não conhece a sigla
// e demora fazendo associação — aqui reverte pra o nome completo. Não
// muda o dado no pedido: é só reformatação na renderização.
const COR_EXPANDIR = {
  BRC: 'Branco',   PRT: 'Preto',    CVL: 'Carvalho', NGR: 'Nogueira',
  CNZ: 'Cinza',    AMD: 'Amadeirado', GFT: 'Grafite',  NDE: 'Nude',
  ARE: 'Areia',    NAT: 'Natural',  OFF: 'Off',      CRM: 'Creme',
  MRM: 'Marrom',   AZL: 'Azul',     VRD: 'Verde',    RSA: 'Rosa',
  VRM: 'Vermelho',
}
const RE_COR_ABREV = /\b(BRC|PRT|CVL|NGR|CNZ|AMD|GFT|NDE|ARE|NAT|OFF|CRM|MRM|AZL|VRD|RSA|VRM)\b/g

export function expandirCoresParaEntrega(texto) {
  if (!texto) return texto
  return String(texto).replace(RE_COR_ABREV, m => COR_EXPANDIR[m] || m)
}
